import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Upload, Loader2, X, FileText, Check } from 'lucide-react'
import { createCase, bulkCreateNodes } from '../lib/api'
import { inferNodes } from '../lib/nodeEngine'
import { uploadFileToStorage, performOCR, extractDocInfo } from '../lib/ocr'
import type { Case } from '../types'

export default function NewCase() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [ocrLoading, setOcrLoading] = useState(false)
  const [ocrResult, setOcrResult] = useState<string | null>(null)
  const [ocrError, setOcrError] = useState<string | null>(null)
  const [showOcrModal, setShowOcrModal] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    caseType: 'civil_litigation',
    partyRole: 'plaintiff',
    opposingParty: '',
    court: '',
    claimAmount: '',
    filingDate: '',
    caseNumber: '',
    notes: '',
    status: 'pending' as 'pending' | 'active' | 'closed' | 'suspended',
  })
  const [error, setError] = useState('')

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (field === 'caseType' && value === 'trademark_admin') {
      setFormData(prev => ({ ...prev, court: '北京知识产权法院' }))
    }
  }

  const handleFileUpload = async (file: File) => {
    if (!file) return
    
    setOcrLoading(true)
    setOcrError(null)
    setOcrResult(null)
    
    try {
      // 直接调用百度 OCR，不需要先上传到 Storage
      const ocrText = await performOCR(file, 'baidu')
      
      setOcrResult(ocrText.text)
      
      // 尝试解析案件信息
      const docInfo = extractDocInfo(ocrText.text, '起诉状')
      
      // 自动填充识别到的信息
      if (docInfo.court) {
        updateField('court', docInfo.court)
      }
      if (docInfo.party) {
        updateField('opposingParty', docInfo.party)
      }
      if (docInfo.amount) {
        updateField('claimAmount', docInfo.amount)
      }
      if (docInfo.docDate) {
        updateField('filingDate', docInfo.docDate)
      }
    } catch (err: any) {
      console.error('OCR 失败:', err)
      setOcrError(err?.message || 'OCR 识别失败，请重试')
    } finally {
      setOcrLoading(false)
    }
  }

  const handleSubmit = async () => {
    const isPending = formData.status === 'pending'
    
    if (!formData.title || !formData.opposingParty || !formData.court) {
      setError('请填写案由、对方当事人、法院')
      return
    }
    
    if (!isPending && !formData.filingDate) {
      setError('请填写立案日期')
      return
    }
    
    setLoading(true)
    setError('')
    
    try {
      const caseData = {
        case_number: isPending ? null : (formData.caseNumber || null),
        title: formData.title,
        case_type: formData.caseType as 'civil_litigation' | 'trademark_admin' | 'other',
        party_role: formData.partyRole as 'plaintiff' | 'defendant' | 'third_party',
        opposing_party: formData.opposingParty,
        court: formData.court,
        claim_amount: formData.claimAmount ? parseFloat(formData.claimAmount) : null,
        status: formData.status as 'pending' | 'active' | 'closed' | 'suspended',
        notes: formData.notes || null,
        filing_date: isPending ? null : formData.filingDate,
        deleted_at: null,
      }
      
      const createdCase = await createCase(caseData)

      // 生成并创建程序节点（仅当有立案日期时）
      if (formData.filingDate) {
        const mockCase: Case = {
          id: '',
          user_id: '',
          case_number: caseData.case_number,
          title: caseData.title,
          case_type: caseData.case_type,
          party_role: caseData.party_role,
          opposing_party: caseData.opposing_party,
          court: caseData.court,
          claim_amount: caseData.claim_amount,
          status: caseData.status,
          notes: caseData.notes,
          filing_date: caseData.filing_date,
          deleted_at: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }

        const nodes = inferNodes(mockCase)
        if (nodes.length > 0) {
          const nodesToInsert = nodes.map(n => ({
            case_id: createdCase.id,
            node_name: n.node_name || '',
            duration_days: n.duration_days || 0,
            start_date: n.start_date || '',
            end_date: n.end_date || '',
            applicable_role: n.applicable_role || null,
            trigger_doc_id: n.trigger_doc_id || null,
            status: n.status as 'upcoming' | 'active' | 'expiring' | 'expired' | 'completed' || 'upcoming',
            action_required: n.action_required || '',
            priority: n.priority as 'low' | 'medium' | 'high' | 'critical' || 'medium',
            notes: n.notes || null,
            completed_at: null,
          }))
          await bulkCreateNodes(nodesToInsert)
        }
      }

      navigate('/cases')
    } catch (err: any) {
      console.error('创建案件失败:', err)
      setError(err?.message || '创建失败，请重试')
    } finally {
      setLoading(false)
    }
  }

  const getStatusHint = () => {
    switch (formData.status) {
      case 'pending':
        return '案件尚未立案，仅填写基本信息即可'
      case 'active':
        return '案件已立案，请填写完整信息'
      case 'closed':
        return '案件已结案，请填写完整信息包括结案日期'
      case 'suspended':
        return '案件已中止，请填写完整信息'
      default:
        return ''
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* OCR 上传按钮 */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowOcrModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
        >
          <Upload size={16} />
          OCR 识别上传
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-8">
        <h2 className="text-xl font-semibold mb-1">新建案件</h2>
        <p className="text-sm text-gray-500 mb-2">
          {formData.status === 'pending' ? '填写案件基本信息，系统将自动创建初始程序节点。' : '请填写完整的案件信息。'}
        </p>
        <p className="text-xs text-indigo-600 mb-4">{getStatusHint()}</p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* 案件状态选择 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">案件状态 <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { value: 'pending', label: '待发起', desc: '尚未立案' },
                { value: 'active', label: '进行中', desc: '已立案' },
                { value: 'closed', label: '已结案', desc: '已结案' },
                { value: 'suspended', label: '中止', desc: '中止审理' },
              ].map(s => (
                <button
                  key={s.value}
                  onClick={() => updateField('status', s.value)}
                  className={`py-2.5 rounded-lg text-sm font-medium transition-all border-2 ${
                    formData.status === s.value
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <div>{s.label}</div>
                  <div className="text-xs opacity-60">{s.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">案由 <span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="如：买卖合同纠纷"
                value={formData.title}
                onChange={e => updateField('title', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">案件类型 <span className="text-red-500">*</span></label>
              <select
                value={formData.caseType}
                onChange={e => updateField('caseType', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              >
                <option value="civil_litigation">民事诉讼</option>
                <option value="trademark_admin">商标行政诉讼</option>
                <option value="other">其他</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">我方角色 <span className="text-red-500">*</span></label>
            <div className="flex gap-3">
              {[
                { value: 'plaintiff', label: '原告', active: formData.partyRole === 'plaintiff' },
                { value: 'defendant', label: '被告', active: formData.partyRole === 'defendant' },
                { value: 'third_party', label: '第三人', active: formData.partyRole === 'third_party' },
              ].map(role => (
                <button
                  key={role.value}
                  onClick={() => updateField('partyRole', role.value)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all border-2 ${
                    role.active
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {role.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">对方当事人 <span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="对方全称"
                value={formData.opposingParty}
                onChange={e => updateField('opposingParty', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">法院 <span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="法院全称"
                value={formData.court}
                onChange={e => updateField('court', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* 立案日期 - 非待发起状态显示 */}
          {formData.status !== 'pending' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">立案日期 <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={formData.filingDate}
                  onChange={e => updateField('filingDate', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">案号</label>
                <input
                  type="text"
                  placeholder="如：（2025）沪01民终1234号"
                  value={formData.caseNumber}
                  onChange={e => updateField('caseNumber', e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          )}

          {/* 待发起状态显示提示 */}
          {formData.status === 'pending' && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                💡 提示：待发起状态只需填写基本信息，立案日期和案号可后续补充。
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">标的金额（元）</label>
              <input
                type="number"
                placeholder="1000000"
                value={formData.claimAmount}
                onChange={e => updateField('claimAmount', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
              <input
                type="text"
                placeholder="简要说明"
                value={formData.notes}
                onChange={e => updateField('notes', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          {/* 自动生成的程序节点预览 */}
          {formData.status === 'pending' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-green-600" />
                <span className="text-sm font-medium text-green-800">自动生成的程序节点预览</span>
              </div>
              <div className="text-xs text-green-700">
                基于你选择的角色和案件类型，立案后将自动生成以下程序节点：
              </div>
              <div className="mt-2 space-y-1">
                {formData.caseType === 'civil_litigation' ? (
                  <>
                    <div className="text-green-700">✅ 法院审查立案（7日）</div>
                    <div className="text-green-700">✅ 送达被告（5日内）</div>
                    <div className="text-green-700">✅ 诉讼费缴纳（7日）</div>
                    <div className="text-green-700">✅ 举证期（15日）</div>
                  </>
                ) : (
                  <>
                    <div className="text-green-700">✅ 起诉期限（15日）</div>
                    <div className="text-green-700">✅ 送达被告（5日内）</div>
                    <div className="text-green-700">✅ 诉讼费缴纳（7日）</div>
                    <div className="text-green-700">✅ 答辩期（15日）</div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={() => navigate('/cases')}
            className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? '创建中...' : '创建案件'}
          </button>
        </div>
      </div>

      {/* OCR 识别弹窗 */}
      {showOcrModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">OCR 识别上传</h3>
              <button
                onClick={() => { setShowOcrModal(false); setOcrResult(null); setOcrError(null); }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {/* 上传区域 */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-500 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleFileUpload(file)
                  }}
                  className="hidden"
                  id="ocr-file-upload"
                />
                <label
                  htmlFor="ocr-file-upload"
                  className="cursor-pointer"
                >
                  {ocrLoading ? (
                    <div className="flex flex-col items-center">
                      <Loader2 size={48} className="text-indigo-600 animate-spin mb-4" />
                      <p className="text-gray-600">正在识别...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload size={48} className="text-gray-400 mb-4" />
                      <p className="text-gray-600 font-medium mb-2">点击上传诉讼文书</p>
                      <p className="text-sm text-gray-400">支持 PDF、JPG、PNG 格式</p>
                    </div>
                  )}
                </label>
              </div>

              {/* 错误提示 */}
              {ocrError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {ocrError}
                </div>
              )}

              {/* 识别结果 */}
              {ocrResult && (
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Check size={16} className="text-green-600" />
                    <span className="text-sm font-medium text-green-700">识别成功</span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-auto">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap">{ocrResult}</pre>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => setShowOcrModal(false)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                    >
                      使用识别结果
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
